/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Fragment, ReactNode, useState } from "react";
import { DeleteCourse } from "../../../api/Courses/DeleteCourse";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "../../../main";
import { Menu, Dialog, Transition } from "@headlessui/react";
import { toast } from "react-toastify";
import Input from "../../Shared/Input";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AddDiscount } from "../../../api/Courses/AddDiscount";
import { AddOfflineCourseDiscount } from "../../../api/OfflineCourses/AddOfflineCourseDiscount";
import { DeleteOfflineCourse } from "../../../api/OfflineCourses/DeleteOfflineCourse";
import { useUserDataStore } from "../../../stores/user";
import { CloneCourseRevision } from "../../../api/Courses/CloneCourseRevision";
import {
  BarChart3,
  BookOpen,
  ClipboardList,
  Copy,
  FileText,
  GraduationCap,
  Percent,
  Pencil,
  SkipForward,
  Snowflake,
  Trash2,
  Video,
} from "lucide-react";
import {
  DashboardActionMenuPanel,
  DashboardMenuButton,
  DashboardMenuLink,
  dangerDialogButtonClass,
  dashboardActionMenuButtonClass,
  dialogCenterClass,
  dialogOverlayClass,
  dialogPanelClass,
  dialogTextClass,
  dialogTitleClass,
  primaryDialogButtonClass,
  subtleDialogButtonClass,
} from "../Shared/DashboardActionMenu";
const validationSchema = Yup.object({
  discountPrecentage: Yup.number().required("Discount Precentage is required"),
});

const CourseDropdown = ({
  children,
  courseId,
  slug,
  refetchCoursesRevision,
}: {
  children?: ReactNode;
  courseId: string;
  slug: string;
  refetchCoursesRevision?: () => void;
}) => {
  const { userData } = useUserDataStore();

  const { mutate: deleteCourse } = useMutation({
    mutationFn: slug ? DeleteCourse : DeleteOfflineCourse,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      queryClient.invalidateQueries({ queryKey: ["offlineCourses"] });
      toast.success("Course deleted successfully");
    },
  });

  const { mutate: cloneCourse } = useMutation({
    mutationFn: CloneCourseRevision,
    onSuccess: () => {
      if (refetchCoursesRevision) refetchCoursesRevision();
      toast.success("Course cloned successfully");
    },
  });

  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { mutate } = useMutation({
    mutationFn: slug ? AddDiscount : AddOfflineCourseDiscount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      queryClient.invalidateQueries({ queryKey: ["offlineCourses"] });

      toast.success("Discount Added successfully");
      formik.resetForm();
      setIsOpen(false);
    },
    onError: () => {
      formik.setSubmitting(false);
    },
  });

  const formik = useFormik({
    initialValues: {
      discountPrecentage: "",
    },
    validationSchema,
    onSubmit: (values) => {
      if (courseId) {
        mutate({
          id: courseId,
          payload: {
            ...values,
            discountPrecentage: Number(values.discountPrecentage),
          },
        });
      }
    },
  });

  return (
    <div className="relative ">
      <Menu as="div" className="relative inline-block">
        <div>
          <Menu.Button className={dashboardActionMenuButtonClass}>
            {children}
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <DashboardActionMenuPanel>
            {userData?.role === "admin" && (
              <>
                <DashboardMenuLink
                  icon={Pencil}
                  to={slug ? `edit-course/${slug}` : `edit-offline-course/${courseId}`}
                >
                  Edit Course
                </DashboardMenuLink>
                {slug && (
                  <>
                    <DashboardMenuButton icon={Copy} onClick={() => cloneCourse(courseId)}>
                      Clone For Revision
                    </DashboardMenuButton>
                    <DashboardMenuLink icon={FileText} to={`edit-course-content/${courseId}`}>
                      Edit Course Content
                    </DashboardMenuLink>
                    <DashboardMenuLink icon={ClipboardList} to={`add-quiz/${courseId}`}>
                      Add Quiz
                    </DashboardMenuLink>
                    <DashboardMenuLink icon={SkipForward} to={`/dashboard/skip-quiz/${courseId}`}>
                      Skip Quiz
                    </DashboardMenuLink>
                    <DashboardMenuLink icon={BookOpen} to={`${courseId}/cbq-exams`}>
                      Edit CBQ Quiz
                    </DashboardMenuLink>
                    <DashboardMenuLink icon={GraduationCap} to={`${courseId}/ifrs-exam`}>
                      Add IFRS Exam
                    </DashboardMenuLink>
                  </>
                )}
                <DashboardMenuLink
                  icon={Video}
                  to={slug ? `add-lecture/${courseId}` : `add-offline-lecture/${courseId}`}
                >
                  Add Lecture
                </DashboardMenuLink>
              </>
            )}
            <DashboardMenuButton icon={Percent} onClick={() => setIsOpen(true)}>
              Add Discount
            </DashboardMenuButton>
            {slug && (
              <>
                <DashboardMenuLink icon={BarChart3} to={`student-progress/${courseId}`}>
                  Student Progress
                </DashboardMenuLink>
                <DashboardMenuLink icon={Snowflake} to={`freeze-student/${courseId}`}>
                  Freeze Student
                </DashboardMenuLink>
              </>
            )}

            {userData?.role === "admin" && (
              <>
                {slug && (
                  <DashboardMenuLink danger icon={Trash2} to={`delete-quiz/${courseId}`}>
                    Delete Quiz
                  </DashboardMenuLink>
                )}

                <DashboardMenuButton danger icon={Trash2} onClick={() => setIsDeleteOpen(true)}>
                  Delete Course
                </DashboardMenuButton>
              </>
            )}

            {userData?.role === "sales" && slug && (
              <DashboardMenuLink icon={SkipForward} to={`/sales/dashboard/skip-quiz/${courseId}`}>
                Skip Quiz
              </DashboardMenuLink>
            )}
          </DashboardActionMenuPanel>
        </Transition>
      </Menu>
      <Transition
        enter="ease-out duration-300"
        enterFrom="opacity-0 transform-[scale(95%)]"
        enterTo="opacity-100 transform-[scale(100%)]"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 transform-[scale(100%)]"
        leaveTo="opacity-0 transform-[scale(95%)]"
        appear
        show={isOpen}
      >
        <Dialog
          as="div"
          className="relative z-10 focus:outline-none"
          onClose={() => setIsOpen(false)}
        >
          <div className={dialogOverlayClass}>
            <div className={dialogCenterClass}>
              <form
                onSubmit={formik.handleSubmit}
                className={dialogPanelClass}
              >
                <h3 className={dialogTitleClass}>Add Discount</h3>
                <p className={dialogTextClass}>
                  How much do you want your discount to be
                </p>
                <Input
                  value={formik.values.discountPrecentage}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.discountPrecentage &&
                    formik.errors.discountPrecentage
                      ? formik.errors.discountPrecentage
                      : ""
                  }
                  className="w-full"
                  inputStyles=" !h-[50px] !rounded-2xl !border-[#DFEAF2] hover:!border-primary"
                  name="discountPrecentage"
                  placeholder=""
                />

                <div className="mt-5 flex justify-center">
                  <button className={`${primaryDialogButtonClass} w-full sm:w-40`}>
                    Add Discount
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition
        enter="ease-out duration-300"
        enterFrom="opacity-0 transform-[scale(95%)]"
        enterTo="opacity-100 transform-[scale(100%)]"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 transform-[scale(100%)]"
        leaveTo="opacity-0 transform-[scale(95%)]"
        appear
        show={isDeleteOpen}
      >
        <Dialog
          as="div"
          className="relative z-10 focus:outline-none"
          onClose={() => setIsDeleteOpen(false)}
        >
          <div className={dialogOverlayClass}>
            <div className={dialogCenterClass}>
              <Dialog.Panel className={dialogPanelClass}>
                <Dialog.Title className={dialogTitleClass}>
                  Delete course
                </Dialog.Title>
                <p className={dialogTextClass}>
                  This permanently deletes the course and its content. This
                  can't be undone. Are you sure?
                </p>
                <div className="mt-4 flex justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => setIsDeleteOpen(false)}
                    className={`${subtleDialogButtonClass} flex-1`}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      deleteCourse(courseId);
                      setIsDeleteOpen(false);
                    }}
                    className={`${dangerDialogButtonClass} flex-1`}
                  >
                    Delete Course
                  </button>
                </div>
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default CourseDropdown;
